import { FontRightKey } from './Bucket';
import { IDjangoCoupon } from './Coupon';
import { IFontStyleModel } from './FontStyles';
import { IDjangoApi1License } from './Licenses';
import { ISubtotal } from './Pricing';

// this is the model that is persisted as json to django.
export interface ICartAddress {
    name: string;
    first_name: string;
    last_name: string;
    country: string;
    company: string;
    zipcode: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    vat?: string;
}

export class CartAddress implements ICartAddress {
    public address1 = '';
    public address2 = '';
    public city = '';
    public company = '';
    public country = '';
    public first_name = '';
    public last_name = '';
    public name = '';
    public state = '';
    public zipcode = '';
    public vat;
}

// these are the qty, term, and additional flags for the cart licenses.
export interface ICartFontRight {
    // app license, TODO: rename to qty
    app?: number | 'unlimited';
    // desktop license, TODO: rename to qty
    computers?: number | 'unlimited';
    // epub license, TODO: rename to qty
    ebooks?: number | 'unlimited';
    // web license, TODO: rename to qty
    views?: number | 'unlimited';
    self_hosted?: boolean;
    years?: number | 'unlimited';
    // indicates a renewal if present and true.
    renew?: boolean;
}

export interface ICartAppRights extends ICartFontRight {
    apps: number | 'unlimited';
    years: number | 'unlimited';
}

export interface ICartDesktopRights extends ICartFontRight {
    computers: number | 'unlimited';
}

export interface ICartEpubRights extends ICartFontRight {
    ebooks: number | 'unlimited';
}

export interface ICartWebRights extends ICartFontRight {
    views: number | 'unlimited';
    years: number | 'unlimited';
    self_hosted?: boolean;
}

export function getCartRightQty(right: ICartFontRight, licenseType) {
    if (licenseType === 'desktop') {
        return right.computers;
    }
    if (licenseType == 'app') {
        return right.app;
    }
    if (licenseType == 'epub') {
        return right.ebooks;
    }
    return right.views;
}

export interface ICartFontRights {
    app: ICartAppRights;
    desktop: ICartDesktopRights;
    epub: ICartEpubRights;
    web: ICartWebRights;
}

export interface ICartLicense {
    font: IFontStyleModel;
    fontRights: ICartFontRights;
    // added to ICartLicense when it is determined to be a part of a package.
    packageBasePrice?: number;
}

export interface IDjangoCartPost {
    name: string;
    cart_data: ICartData;
}

export interface ICartData {
    license: ICartLicense[];
}

export interface IDjangoCart {
    id: number;
    date: string;
    user: number;
    name: string;
    cart_data: ICartData;
}

// TODO: refactor so IDjagoCart.cart_data.license and IClientCart.license have the same hierarchy.
export interface IClientCart extends ICartData {
    // id, user, and date are only present on saved carts.
    id: number;
    date: Date;
    user: number;
    title: string;
    licensee?: ICartAddress;
    // this seems to be calculated in localCarts, we may want to hoist this up into the repository transform
    // function, but I'm not sure I want to make the repository dependent on the pricing service. - dopry
    subtotal?: ISubtotal;
    // I'm not sure if this is actually stored on the cart. It was used in localCarts to call calculateTotals.
    // I believe coupon is only on order.
    // TODO: review usage and remove if not used.
    coupon?: IDjangoCoupon;
}

export interface ICartTotals {
    annual: {
        base: number;
        discounted: number;
    };
    coupon: number;
    finals: {
        withoutTax: number;
        withTax: number;
    };
    fiveYears: {
        base: number;
        discounted: number;
    };
    oneTime: {
        base: number;
        discounted: number;
    };
    subTotal: {
        base: number;
        discounted: number;
    };
    threeYears: {
        base: number;
        discounted: number;
    };
}

export const EMPTY_CART_TOTALS: ICartTotals = {
    annual: {
        base: 0,
        discounted: 0,
    },
    coupon: 0,
    finals: {
        withoutTax: 0,
        withTax: 0,
    },
    fiveYears: {
        base: 0,
        discounted: 0,
    },
    oneTime: {
        base: 0,
        discounted: 0,
    },
    subTotal: {
        base: 0,
        discounted: 0,
    },
    threeYears: {
        base: 0,
        discounted: 0,
    },
};

export interface ICartEula {
    accepeted: boolean;
    eula: string;
    types: FontRightKey[][];
}

export class CartModel {
    public id: number | null = null;
    public billingAddress?: unknown = null;
    public card?: unknown = null;
    public coupon?: IDjangoCoupon = undefined;
    public license: ICartLicense[] = [];
    public licensee = new CartAddress();
    public invoice = false;
    public stripeToken?: unknown = null;
    public title?: unknown = null;
    public payments?: unknown = null;
    public totals?: ICartTotals;
    public eula?: Record<number, ICartEula> = {};
}

export interface ILicenseUpgradeDict {
    [fontId: number]: IDjangoApi1License;
}

export interface ICartFilters {
    licenseType?: string;
    title?: string;
    date?: {
        start?: {
            month: number;
            year: number;
        };
        end?: {
            month: number;
            year: number;
        };
    };
    cost?: {
        min: number;
        max: number;
    };
}

export function cartHasLicenseType(
    cart: IClientCart,
    type: FontRightKey,
    qtyProp: string
): boolean {
    return cart.license.some((license) => {
        const right = license.fontRights[type];
        if (!right) return false;
        const qty = right[qtyProp];
        if (qty === 'unlimited') return true;
        return qty > 0;
    });
}
export function filterCart(cart: IClientCart, filter: ICartFilters): boolean {
    const licenseType = filter.licenseType;

    if (licenseType && licenseType.length !== 4 && licenseType.length > 0) {
        // if the cart doesn't have any licenses it can't match, so exit early.
        if (cart.license.length == 0) return false;
        if (
            licenseType.includes('Desktop') &&
            !cartHasLicenseType(cart, 'desktop', 'computers')
        )
            return false;
        if (
            licenseType.includes('Web') &&
            !cartHasLicenseType(cart, 'web', 'views')
        )
            return false;
        if (
            licenseType.includes('Application') &&
            !cartHasLicenseType(cart, 'app', 'apps')
        )
            return false;
        if (
            licenseType.includes('ePub') &&
            !cartHasLicenseType(cart, 'epub', 'ebooks')
        )
            return false;
    }

    const title = filter.title?.toLowerCase();
    if (title && title.length > 0) {
        if (
            !cart.title.toLowerCase().includes(title) &&
            !cart.license.some((license) =>
                license.font.name.toLowerCase().includes(title)
            )
        )
            return false;
    }

    const startYear = filter.date?.start?.year;
    const startMonth = filter.date?.start?.month;
    if (startMonth && startYear) {
        const startDate = new Date(startYear, startMonth);
        if (startDate > new Date(cart.date)) return false;
    }

    const endYear = filter.date?.end?.year;
    const endMonth = filter.date?.end?.month;
    if (endMonth && endYear) {
        const endDate = new Date(endYear, endMonth);
        if (endDate < new Date(cart.date)) return false;
    }

    const minCost = filter.cost?.min;
    if (minCost) {
        if (cart.subtotal && cart.subtotal.discounted < minCost) return false;
    }

    const maxCost = filter.cost?.max;
    if (maxCost) {
        if (cart.subtotal && cart.subtotal.discounted > maxCost) return false;
    }
    return true;
}
